import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PERMISSIONS } from './types';

import MainNavigation from './components/MainNavigation';
import Login from './pages/Login';

// Lazy load components
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Configuration = React.lazy(() => import('./pages/Configuration'));
const PriceManagement = React.lazy(() => import('./pages/prices/PriceManagement'));
const PartsManagement = React.lazy(() => import('./pages/parts/PartsManagement'));
const PartsList = React.lazy(() => import('./pages/parts/PartsList'));
const NewOrder = React.lazy(() => import('./pages/orders/NewOrder'));
const NewDispute = React.lazy(() => import('./pages/disputes/NewDispute'));
const InsuranceDispute = React.lazy(() => import('./pages/disputes/InsuranceDispute'));
const DisputeManagement = React.lazy(() => import('./pages/disputes/DisputeManagement'));
const PaymentTracking = React.lazy(() => import('./pages/payments/PaymentTracking'));

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
    </div>
  );
}

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const handleError = () => setHasError(true);
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Une erreur est survenue</h1>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Rafraîchir la page
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function PrivateRoute({ children, permission }: { children: React.ReactNode, permission?: string }) {
  const { user, loading } = useAuth();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false);
    }
  }, [loading]);

  // Show loading spinner only on initial load
  if (isInitialLoad && loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (permission && !user.permissions.includes(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false);
    }
  }, [loading]);

  // Show loading spinner only on initial load
  if (isInitialLoad && loading) {
    return <LoadingSpinner />;
  }

  if (!user || user.email !== 'info@allo-reparateur.tn') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function Layout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {user && <MainNavigation />}
      <div className="container mx-auto px-4 py-8">
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            {children}
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/configuration/*"
              element={
                <AdminRoute>
                  <Configuration />
                </AdminRoute>
              }
            />
            <Route
              path="/prices/*"
              element={
                <PrivateRoute permission={PERMISSIONS.PRICE_MANAGEMENT}>
                  <PriceManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/parts/management"
              element={
                <PrivateRoute permission={PERMISSIONS.PARTS_MANAGEMENT}>
                  <PartsManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/parts/list"
              element={
                <PrivateRoute permission={PERMISSIONS.PARTS_LIST}>
                  <PartsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/orders/new"
              element={
                <PrivateRoute permission={PERMISSIONS.NEW_ORDER}>
                  <NewOrder />
                </PrivateRoute>
              }
            />
            <Route
              path="/disputes/new"
              element={
                <PrivateRoute permission={PERMISSIONS.NEW_DISPUTE}>
                  <NewDispute />
                </PrivateRoute>
              }
            />
            <Route
              path="/disputes/insurance"
              element={
                <PrivateRoute permission={PERMISSIONS.INSURANCE_DISPUTE}>
                  <InsuranceDispute />
                </PrivateRoute>
              }
            />
            <Route
              path="/disputes/management"
              element={
                <PrivateRoute permission={PERMISSIONS.DISPUTE_MANAGEMENT}>
                  <DisputeManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <PrivateRoute permission={PERMISSIONS.PAYMENT_TRACKING}>
                  <PaymentTracking />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;